import React from 'react';
import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import Button from '@atlaskit/button/default/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { defaultImgSrc, defaultDarkImgSrc } from '../localMappings/templateToImgMappings.ts';
import { useHybridLinkClick } from '../hooks/useHybridLinkClick.ts';

import { messages } from './TemplateModal.i18n.ts';

export type TemplateType = 'page' | 'whiteboard';

export interface TemplateCardProps {
	name: string;
	contentType?: TemplateType;
	contentTypeLabel?: string;
	contentTypeIcon?: React.ReactElement;
	imageSource?: string;
	imageSourceDark?: string;
	createLink?: string;
	templateId?: string;
	onClose?: () => void;
}

const previewContainerStyles = css({
	position: 'relative',
	height: '120px',
	width: '100%',
	overflow: 'hidden',
	borderTopLeftRadius: token('radius.small'),
	borderTopRightRadius: token('radius.small'),
});

const previewImageStyles = css({
	height: '100%',
	width: '100%',
	borderWidth: 0,
	transition: 'filter 0.2s ease',
});

const blurredImageStyles = css({
	filter: 'blur(4px)',
});

const overlayStyles = css({
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	opacity: 0,
	transition: 'opacity 0.2s ease',
	pointerEvents: 'none',
});

const overlayVisibleStyles = css({
	opacity: 1,
});

const headingOverrideStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	width: '100%',
	maxWidth: '100%',
	textAlign: 'left',
});

const cardStyles = cssMap({
	mainContainer: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
		height: '180px',
		padding: 0,
		backgroundColor: token('elevation.surface.raised'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		borderRadius: token('radius.small'),
		textDecoration: 'none',
		'&:hover': {
			boxShadow: token('elevation.shadow.raised'),
			textDecoration: 'none',
		},
	},
	textContainer: {
		display: 'flex',
		flexDirection: 'column',
		padding: token('space.150'),
		gap: token('space.050'),
		backgroundColor: token('elevation.surface.sunken'),
		borderBottomLeftRadius: token('radius.small'),
		borderBottomRightRadius: token('radius.small'),
	},

	subTextContainer: {
		display: 'flex',
		gap: token('space.100'),
	},
});

export const TemplateCard: React.FC<TemplateCardProps> = ({
	name,
	contentType,
	contentTypeLabel,
	contentTypeIcon,
	imageSource,
	imageSourceDark,
	createLink,
	templateId,
	onClose,
}) => {
	const [isHovered, setIsHovered] = React.useState(false);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hybridLinkClick = useHybridLinkClick(createLink);

	const handleClick = React.useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'cfind-5291-template-touchpoint',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'templateCard',
					attributes: {
						templateId,
						contentType,
					},
				},
			}).fire();
			onClose?.();
			hybridLinkClick(e);
		},
		[onClose, hybridLinkClick, templateId, contentType, createAnalyticsEvent],
	);

	return (
		<Anchor
			href={createLink ?? '#'}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={handleClick}
			xcss={cardStyles.mainContainer}
			aria-label={formatMessage(messages.templateCardAriaLabel, {
				templateName: name,
				contentType: contentTypeLabel,
			})}
		>
			<div css={previewContainerStyles}>
				<Image
					src={imageSource ?? defaultImgSrc}
					srcDark={imageSourceDark ?? defaultDarkImgSrc}
					alt=""
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					css={[previewImageStyles, isHovered && blurredImageStyles]}
				/>
				<div css={[overlayStyles, isHovered && overlayVisibleStyles]}>
					<Button appearance="primary" tabIndex={-1} aria-hidden>
						{formatMessage(messages.buttonUseTemplate)}
					</Button>
				</div>
			</div>
			<Box xcss={cardStyles.textContainer}>
				<div css={headingOverrideStyles} title={name}>
					<Text size="large" weight="bold">
						{name}
					</Text>
				</div>
				<Text>
					<Box xcss={cardStyles.subTextContainer}>
						<span>{contentTypeIcon}</span>
						<span>{contentTypeLabel}</span>
					</Box>
				</Text>
			</Box>
		</Anchor>
	);
};
